import api from './api';

export interface Hearing {
  id: number;
  case_id: number;
  case_number?: string;
  client_name?: string;
  title: string;
  type?: string;
  hearing_type: 'initial' | 'evidence' | 'judgment' | 'conciliation' | 'other';
  scheduled_date: string;
  scheduled_time?: string;
  location?: string;
  virtual_link?: string;
  modality?: 'in_person' | 'virtual' | 'hybrid';
  status: 'scheduled' | 'rescheduled' | 'completed' | 'cancelled';
  outcome?: string;
  notes?: string;
  created_by: number;
  created_at?: string;
  updated_at?: string;
}

export interface HearingListParams {
  page?: number;
  limit?: number;
  status?: string;
  case_id?: number;
  from_date?: string;
  to_date?: string;
}

export interface CreateHearingDto {
  case_id: number;
  title: string;
  hearing_type?: string;
  scheduled_date: string;
  scheduled_time?: string;
  location?: string;
  virtual_link?: string;
  notes?: string;
}

const hearingsService = {
  async list(params?: HearingListParams) {
    const response = await api.get('/hearings', { params });
    return {
      data: response.data.data || [],
      pagination: response.data.meta || { page: 1, limit: 20, total: 0, totalPages: 1 }
    };
  },

  async getById(id: number): Promise<Hearing> {
    const response = await api.get(`/hearings/${id}`);
    return response.data.data || response.data;
  },

  async getByCase(caseId: number): Promise<Hearing[]> {
    const response = await api.get(`/hearings/case/${caseId}`);
    return response.data.data || [];
  },

  async getUpcoming(days: number = 7): Promise<Hearing[]> {
    const response = await api.get('/hearings/upcoming', { params: { days } });
    return response.data.data || [];
  },

  async getToday(): Promise<Hearing[]> {
    const response = await api.get('/hearings/today');
    return response.data.data || [];
  },

  async create(data: CreateHearingDto): Promise<Hearing> {
    const response = await api.post('/hearings', data);
    return response.data.data || response.data;
  },

  async update(id: number, data: Partial<CreateHearingDto>): Promise<Hearing> {
    const response = await api.patch(`/hearings/${id}`, data);
    return response.data.data || response.data;
  },

  async setOutcome(id: number, outcome: string, notes?: string): Promise<Hearing> {
    const response = await api.post(`/hearings/${id}/outcome`, { outcome, notes });
    return response.data.data || response.data;
  },

  async postpone(id: number, newDate: string, newTime?: string, reason?: string): Promise<Hearing> {
    const response = await api.post(`/hearings/${id}/postpone`, { new_date: newDate, new_time: newTime, reason });
    return response.data.data || response.data;
  },

  async cancel(id: number, reason?: string): Promise<Hearing> {
    const response = await api.post(`/hearings/${id}/cancel`, { reason });
    return response.data.data || response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/hearings/${id}`);
  }
};

export default hearingsService;
